import { createContext, useContext, useId, useLayoutEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";

export interface Crumb {
  label: string;
  href?: string;
}

export type HeaderLayout = "page" | "hero" | "full";
export type HeaderSensitivity = "public" | "internal" | "restricted" | "confidential";

/**
 * What a screen tells the frame about itself (shell-ia §13.1). The legacy shell and the os window strip read `crumbs`, `title` and
 * `actions`; the ECONIC frame draws the page header from the rest too (`usePageHeader` in econic/shell/PageHeader.tsx is this hook).
 */
export interface PageChrome {
  crumbs?: Crumb[];
  /** The screen's own name ("STD-2026-000009 · Tourism and the UAE economy"). */
  title?: string;
  actions?: ReactNode;
  /** Small caps line above the title (Latin only; plain gold text in Arabic). */
  eyebrow?: string;
  subtitle?: string;
  /** "● Live · 09:42" | "Awaiting MoET input" | a deliverable chip. */
  status?: ReactNode;
  /** A sticky 34 px row of sub-tabs. */
  tabs?: { id: string; label: string; href: string; count?: number }[];
  /** Raises the classification banner while the screen is shown (shortcut for `useClassification`). */
  classification?: HeaderSensitivity;
  /** The ✦ "Ask about this screen" prompt the sidebar receives. */
  ask?: string;
  /** "hero": the screen draws its own top (Today); "full": no gutters and no header (desks, maps, the report studio). */
  layout?: HeaderLayout;
  /** Shows "Export PDF" in the header (WeasyPrint through the report service). */
  exportPdf?: () => Promise<void> | void;
}

/** The registered chromes in mount order; the top bar shows the most recent, so a screen opened over another (a kept app frame) wins. */
class ChromeStore {
  private entries = new Map<string, PageChrome>();
  private listeners = new Set<() => void>();
  private top: PageChrome | null = null;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  current = () => this.top;

  set(id: string, chrome: PageChrome) {
    this.entries.set(id, chrome);
    this.emit();
  }

  delete(id: string) {
    if (this.entries.delete(id)) this.emit();
  }

  private emit() {
    this.top = [...this.entries.values()].at(-1) ?? null;
    for (const l of this.listeners) l();
  }
}

const ChromeContext = createContext<ChromeStore | null>(null);

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const store = useMemo(() => new ChromeStore(), []);
  return <ChromeContext.Provider value={store}>{children}</ChromeContext.Provider>;
}

/** Puts the screen's breadcrumbs (after the space), title and actions in the frame for as long as the screen is mounted. */
export function usePageChrome(chrome: PageChrome | null): void {
  const store = useContext(ChromeContext);
  const id = useId();
  useLayoutEffect(() => {
    if (store && chrome) store.set(id, chrome);
    else store?.delete(id);
  });
  useLayoutEffect(() => () => store?.delete(id), [store, id]);
}

/** The ECONIC name of the same hook (shell-ia §13.1). */
export const usePageHeader = usePageChrome;

export function useCurrentChrome(): PageChrome | null {
  const store = useContext(ChromeContext);
  return useSyncExternalStore(store?.subscribe ?? noopSubscribe, store?.current ?? nothing);
}

const noopSubscribe = () => () => undefined;
const nothing = () => null;
